import { useId, useMemo } from "react";
import { Alert, Button, TextField } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckIcon, WarningIcon } from "@phosphor-icons/react";
import { useTranslations } from "use-intl";
import { IconPromoCode } from "../icons";

const createValidationSchema = (t: ReturnType<typeof useTranslations>) =>
  z.object({
    code: z.string().trim().min(1, t("promoCode.validation.required")),
  });

type PromoCodeFormValues = z.infer<ReturnType<typeof createValidationSchema>>;
export type ValidationStatus = {
  type: "success" | "error" | "loading";
  message?: string;
};

export type Props = {
  appliedCode?: string;
  validationStatus?: ValidationStatus;
  onCodeApply: (values: PromoCodeFormValues) => void;
  onCodeRemove: () => void;
};

export function PromoCodeForm({
  appliedCode,
  validationStatus,
  onCodeApply,
  onCodeRemove,
}: Props) {
  const t = useTranslations();
  const headerId = useId();

  const promoCodeFormSchema = useMemo(() => createValidationSchema(t), [t]);

  const {
    handleSubmit,
    formState: { isValid },
    reset,
    control,
  } = useForm<
    z.input<typeof promoCodeFormSchema>,
    undefined,
    PromoCodeFormValues
  >({
    resolver: zodResolver(promoCodeFormSchema),
    defaultValues: {
      code: appliedCode || "",
    },
  });

  const isLoading = validationStatus?.type === "loading";

  return (
    <div className="section-card">
      <div
        id={headerId}
        className="mb-4 flex items-center gap-3 text-base font-medium text-text-primary"
      >
        <IconPromoCode
          className="size-10 shrink-0 rounded-lg bg-surface-accent-soft p-2 text-surface-accent-strong"
          aria-hidden
        />
        {t("promoCode.title")}
      </div>

      <form onSubmit={handleSubmit(onCodeApply)}>
        <Controller
          name="code"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              aria-labelledby={headerId}
              hiddenLabel
              placeholder={t("promoCode.placeholder")}
              disabled={isLoading}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  readOnly: !!appliedCode,
                  endAdornment:
                    appliedCode && !isLoading ? (
                      <Button
                        key="remove-button"
                        type="button"
                        variant="text"
                        onClick={() => {
                          onCodeRemove();
                          reset({ code: "" });
                        }}
                      >
                        {t("remove")}
                      </Button>
                    ) : (
                      <Button
                        key="apply-button"
                        type="submit"
                        variant="text"
                        disabled={!isValid}
                        loading={isLoading}
                      >
                        {t("submit")}
                      </Button>
                    ),
                },
              }}
            />
          )}
        />

        {validationStatus?.type === "success" ||
        validationStatus?.type === "error" ? (
          <Alert
            severity={validationStatus.type}
            sx={{
              marginTop: "calc(var(--spacing) * 3)",
              alignItems: "center",
              ...(validationStatus.type === "success"
                ? {
                    fontWeight: "var(--font-weight-bold)",
                    color: "var(--color-core-rahal-400)",
                  }
                : { fontWeight: "var(--font-weight-medium)" }),
            }}
            iconMapping={{
              success: (
                <CheckIcon
                  className="size-8 animate-pop-in rounded-full bg-surface-accent-soft p-1.5 text-surface-accent-strong"
                  weight="bold"
                  aria-hidden
                />
              ),
              error: (
                <WarningIcon className="size-6" weight="bold" aria-hidden />
              ),
            }}
          >
            {validationStatus.message}
          </Alert>
        ) : null}
      </form>
    </div>
  );
}
